import { useContext, useEffect, useRef } from 'react';
import {
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
} from '@chakra-ui/react';
import { TriangleDownIcon, CheckIcon, CloseIcon } from '@chakra-ui/icons';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectFilterValues,
  updateFilterValues,
  initialFilterValues,
} from '../../../store/redux/slices/templateList.slice';
import { MeContext } from '../../../context/MeContext';

const STATUS_OPTIONS = [
  { label: 'Draft', value: 'draft' },
  { label: 'Published', value: 'published' },
];

const PASS_TYPE_OPTIONS = [
  { label: 'Generic', value: 'generic' },
  { label: 'Loyalty', value: 'storeCard' },
  { label: 'Event', value: 'eventTicket' },
  { label: 'Stamp card', value: 'stampCard' },
];

interface MeContextType {
  state: {
    filterEntity?: { uuid?: string } | null;
  };
}

function PassTemplatesListFilter() {
  const dispatch = useDispatch();
  const filterValues = useSelector(selectFilterValues);
  const meCtx = useContext(MeContext) as MeContextType;
  const { filterEntity } = meCtx.state;
  const filterEntityRef = useRef(filterEntity);

  useEffect(() => {
    if (filterEntity !== filterEntityRef.current) {
      filterEntityRef.current = filterEntity;
      dispatch(updateFilterValues({ ...initialFilterValues }));
    }
  }, [filterEntity, dispatch]);

  const selectedStatus = filterValues.status || '';
  const selectedPassType = filterValues.passType || '';
  const hasStatusFilter = Boolean(selectedStatus);
  const hasPassTypeFilter = Boolean(selectedPassType);

  const setStatus = (status: string) => {
    dispatch(
      updateFilterValues({
        ...filterValues,
        status: filterValues.status === status ? '' : status,
      })
    );
  };

  const clearStatus = () => {
    dispatch(updateFilterValues({ ...filterValues, status: '' }));
  };

  const setPassType = (passType: string) => {
    dispatch(
      updateFilterValues({
        ...filterValues,
        passType: filterValues.passType === passType ? '' : passType,
      })
    );
  };

  const clearPassType = () => {
    dispatch(updateFilterValues({ ...filterValues, passType: '' }));
  };

  const menuButtonProps = (isActive: boolean) => ({
    as: Button,
    variant: 'outline',
    size: 'sm',
    borderRadius: '5px',
    bg: 'white',
    fontWeight: 400,
    color: isActive ? '#505E69' : '#4C4C4C',
    border: isActive ? '1.5px solid #505E69' : 'none',
    _hover: { borderColor: '#505E69' },
    _expanded: { borderBottomRadius: 0, bg: '#F8F8F8' },
  });

  const menuListProps = {
    minW: 'unset',
    w: 'full',
    borderTopRadius: 0,
    borderBottomRadius: '5px',
    border: 'none',
    boxShadow: 'md',
    py: 0,
    overflow: 'hidden' as const,
  };

  const menuItemProps = {
    fontSize: '13px',
    textStyle: 'bodyFamilyRegular',
    color: '#4C4C4C',
    bg: '#F8F8F8',
    _hover: { bg: '#FFFFFF' },
    _focus: { bg: '#F8F8F8', _hover: { bg: '#FFFFFF' } },
  };

  const statusLabel =
    STATUS_OPTIONS.find((o) => o.value === selectedStatus)?.label || 'Status';
  const passTypeLabel =
    PASS_TYPE_OPTIONS.find((o) => o.value === selectedPassType)?.label ||
    'Pass type';

  return (
    <>
      <Menu placement="bottom-start" closeOnSelect gutter={0} matchWidth>
        <Flex align="center" position="relative">
          <MenuButton
            {...menuButtonProps(hasStatusFilter)}
            minW="100px"
            pr={hasStatusFilter ? '43px' : '32px'}
          >
            <Text textTransform="none" letterSpacing="0.01em">
              {statusLabel}
            </Text>
          </MenuButton>
          {hasStatusFilter && (
            <CloseIcon
              aria-label="Clear status filter"
              boxSize="9px"
              position="absolute"
              right="28px"
              cursor="pointer"
              zIndex={1}
              color="#989898"
              _hover={{ color: '#4C4C4C' }}
              onClick={clearStatus}
            />
          )}
          <TriangleDownIcon
            boxSize="10px"
            position="absolute"
            right="10px"
            pointerEvents="none"
          />
        </Flex>
        <MenuList {...menuListProps}>
          {STATUS_OPTIONS.map((option) => {
            const isSelected = selectedStatus === option.value;
            return (
              <MenuItem
                key={option.value}
                {...menuItemProps}
                onClick={() => setStatus(option.value)}
                fontWeight={isSelected ? 600 : 400}
                color={isSelected ? '#505E69' : '#4C4C4C'}
                justifyContent="space-between"
              >
                {option.label}
                {isSelected && <CheckIcon boxSize="10px" />}
              </MenuItem>
            );
          })}
        </MenuList>
      </Menu>

      <Menu placement="bottom-start" closeOnSelect gutter={0} matchWidth>
        <Flex align="center" position="relative">
          <MenuButton
            {...menuButtonProps(hasPassTypeFilter)}
            minW="120px"
            pr={hasPassTypeFilter ? '43px' : '32px'}
          >
            <Text textTransform="none" letterSpacing="0.01em">
              {passTypeLabel}
            </Text>
          </MenuButton>
          {hasPassTypeFilter && (
            <CloseIcon
              aria-label="Clear pass type filter"
              boxSize="9px"
              position="absolute"
              right="28px"
              cursor="pointer"
              zIndex={1}
              color="#989898"
              _hover={{ color: '#4C4C4C' }}
              onClick={clearPassType}
            />
          )}
          <TriangleDownIcon
            boxSize="10px"
            position="absolute"
            right="10px"
            pointerEvents="none"
          />
        </Flex>
        <MenuList {...menuListProps}>
          {PASS_TYPE_OPTIONS.map((option) => {
            const isSelected = selectedPassType === option.value;
            return (
              <MenuItem
                key={option.value}
                {...menuItemProps}
                onClick={() => setPassType(option.value)}
                fontWeight={isSelected ? 600 : 400}
                color={isSelected ? '#505E69' : '#4C4C4C'}
                justifyContent="space-between"
              >
                {option.label}
                {isSelected && <CheckIcon boxSize="10px" />}
              </MenuItem>
            );
          })}
        </MenuList>
      </Menu>
    </>
  );
}

export default PassTemplatesListFilter;
